import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Typography } from "@mui/material";
import logoYoko4c from "assets/icons/logo_yoko_4c.svg";
import { CompanyType, EstateType } from "graphql/API";
import { useCompanyQueryHooks } from "hooks/companyHooks/companyQueryHooks";
import React, { useCallback, useEffect, useMemo } from "react";
import styles from "./DetailPage.subComponents.module.scss";

export const ContentCompany = React.memo(
  (props: {
    company: CompanyType | null | undefined;
    estate: EstateType | null | undefined;
    tel: string | null | undefined;
    companyIndex: string | null | undefined;
    forAnonymous: boolean | null | undefined;
    isReadyToUse: boolean | null | undefined;
    onCompanyIndexChanged: (companyIndex: number) => void;
    onCompanyLoadCompleted: () => void;
  }) => {
    interface CompanyContent {
      id?: string | null;
      name?: string | null;
      address?: string | null;
      tel?: string | null;
      fax?: string | null;
      myTel?: string | null;
      license?: string | null;
      mediationKind?: string | null;
      organization?: string | null;
    }

    const [showCompany, setShowCompany] = React.useState(true);
    const {
      company: distributer,
      isLoading: isDistributerLoading,
      error: distributerError
    } = useCompanyQueryHooks(
      props.company?.id,
      undefined,
      props.forAnonymous && !props.isReadyToUse
    );
    const {
      company: estateCompany,
      isLoading: isEstateCompanyLoading,
      error: estateCompanyError
    } = useCompanyQueryHooks(
      props.estate?.companyId,
      undefined,
      props.forAnonymous && !props.isReadyToUse
    );
    const {
      company: estateSubCompany,
      isLoading: isEstateSubCompanyLoading,
      error: estateSubCompanyError
    } = useCompanyQueryHooks(
      props.estate?.subCompanyId,
      undefined,
      props.forAnonymous && !props.isReadyToUse
    );
    const {
      company: estateSubCompany2,
      isLoading: isEstateSubCompany2Loading,
      error: estateSubCompany2Error
    } = useCompanyQueryHooks(
      props.estate?.subCompany2Id,
      undefined,
      props.forAnonymous && !props.isReadyToUse
    );

    const allCompaniesSettled = useMemo(() => {
      return (
        props.estate &&
        (!isDistributerLoading || distributerError) &&
        (!isEstateCompanyLoading || estateCompanyError) &&
        (!isEstateSubCompanyLoading || estateSubCompanyError) &&
        (!isEstateSubCompany2Loading || estateSubCompany2Error)
      );
    }, [
      props.estate,
      isDistributerLoading,
      distributerError,
      isEstateCompanyLoading,
      estateCompanyError,
      isEstateSubCompanyLoading,
      estateSubCompanyError,
      isEstateSubCompany2Loading,
      estateSubCompany2Error
    ]);

    const companies: CompanyContent[] = useMemo(() => {
      const companies: CompanyContent[] = [];

      if (props.company) {
        companies.push({
          id: props.company?.id,
          name: `${props.company?.name || ""} ${
            props.company?.branchName || ""
          }`,
          address: props.company?.branchAddress || props.company?.address,
          tel: props.company?.branchTel || props.company?.tel,
          fax: props.company?.branchFax || props.company?.fax,
          myTel: props.tel,
          license: props.company?.license,
          mediationKind: "取引態様：媒介",
          organization: distributer?.organization ?? ""
        });
      }

      if (props.estate?.companyId) {
        companies.push({
          id: props.estate?.companyId,
          name: props.estate?.companyName,
          address: props.estate?.companyAddress,
          tel: props.estate?.companyTel,
          fax: props.estate?.companyFax,
          license: props.estate?.companyLicense,
          mediationKind:
            props.estate?.mediationKind !== null &&
            props.estate?.mediationKind !== undefined
              ? `取引態様：${props.estate?.mediationKind}`
              : "取引態様：媒介",
          organization: estateCompany?.organization ?? ""
        });
      }

      if (props.estate?.subCompanyId) {
        companies.push({
          id: props.estate?.subCompanyId,
          name: props.estate?.subCompanyName,
          address: props.estate?.subCompanyAddress,
          tel: props.estate?.subCompanyTel,
          fax: props.estate?.subCompanyFax,
          license: props.estate?.subCompanyLicense,
          mediationKind: "取引態様：媒介",
          organization: estateSubCompany?.organization ?? ""
        });
      }

      if (props.estate?.subCompany2Id) {
        companies.push({
          id: props.estate?.subCompany2Id,
          name: props.estate?.subCompany2Name,
          address: props.estate?.subCompany2Address,
          tel: props.estate?.subCompany2Tel,
          fax: props.estate?.subCompany2Fax,
          license: props.estate?.subCompany2License,
          mediationKind: "取引態様：媒介",
          organization: estateSubCompany2?.organization ?? ""
        });
      }

      companies.push({
        id: null,
        name: null,
        address: null,
        tel: null,
        fax: null,
        license: null,
        mediationKind: null,
        organization: null
      });

      const ids = new Set(companies.map((c) => c.id));

      return Array.from(ids).map((id) => companies.find((c) => c.id === id)!);
    }, [
      distributer?.organization,
      estateCompany?.organization,
      estateSubCompany?.organization,
      estateSubCompany2?.organization,
      props.company,
      props.estate?.companyAddress,
      props.estate?.companyFax,
      props.estate?.companyId,
      props.estate?.companyLicense,
      props.estate?.companyName,
      props.estate?.companyTel,
      props.estate?.mediationKind,
      props.estate?.subCompany2Address,
      props.estate?.subCompany2Fax,
      props.estate?.subCompany2Id,
      props.estate?.subCompany2License,
      props.estate?.subCompany2Name,
      props.estate?.subCompany2Tel,
      props.estate?.subCompanyAddress,
      props.estate?.subCompanyFax,
      props.estate?.subCompanyId,
      props.estate?.subCompanyLicense,
      props.estate?.subCompanyName,
      props.estate?.subCompanyTel,
      props.tel
    ]);

    const company = useMemo(
      () => companies.at(Number(props.companyIndex ?? 0)) ?? {},
      [companies, props.companyIndex]
    );

    const nameSizeClass = useMemo(() => {
      const len = company.name?.length ?? 0;
      if (len <= 15) return "nameTextLen15";
      if (len <= 25) return "nameTextLen25";
      if (len <= 35) return "nameTextLen35";
      return "nameTextLen36";
    }, [company.name]);

    const addressSizeClass = useMemo(() => {
      const len = company.address?.length ?? 0;
      if (len <= 15) return "addressTextLen15";
      if (len <= 25) return "addressTextLen25";
      if (len <= 35) return "addressTextLen35";
      return "addressTextLen36";
    }, [company.address]);

    const onUpClick = useCallback(() => {
      setShowCompany(false);

      const oldCompanyIndex = Number(props.companyIndex ?? 0);
      const newCompanyIndex =
        oldCompanyIndex > 0 ? oldCompanyIndex - 1 : oldCompanyIndex;

      setTimeout(() => {
        props.onCompanyIndexChanged(newCompanyIndex);
        setShowCompany(true);
      }, 200);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companies.length, props.onCompanyIndexChanged, props.companyIndex]);

    const onDownClick = useCallback(() => {
      setShowCompany(false);

      const oldCompanyIndex = Number(props.companyIndex ?? 0);
      const newCompanyIndex =
        oldCompanyIndex < companies.length - 1
          ? oldCompanyIndex + 1
          : oldCompanyIndex;

      setTimeout(() => {
        props.onCompanyIndexChanged(newCompanyIndex);
        setShowCompany(true);
      }, 200);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companies.length, props.onCompanyIndexChanged, props.companyIndex]);

    useEffect(() => {
      if (allCompaniesSettled) {
        props.onCompanyLoadCompleted();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCompaniesSettled]);

    return (
      <Box className={styles.companyContent}>
        <Box
          className={`${styles.name} ${
            showCompany ? styles.visible : styles.hidden
          }`}
        >
          <Typography variant="h6" color="div">
            <Box
              component="div"
              className={`${styles.nameTextBlock} ${styles.nameText} ${styles[nameSizeClass]}`}
            >
              {company.name}
            </Box>
            <Box component="div" className={styles.license}>
              {company.license} <span>{company.mediationKind}</span>
            </Box>
            <Box component="div" className={styles.organization}>
              {(company.organization ?? "") === "" ? "" : "所属団体："}
              {company.organization}
            </Box>
          </Typography>
        </Box>
        <Box
          className={`${styles.address} ${
            showCompany ? styles.visible : styles.hidden
          }`}
        >
          <Typography variant="inherit" component="div">
            <Box
              component="div"
              className={`${styles.addressTextBlock} ${styles.addressText} ${styles[addressSizeClass]}`}
            >
              {company.address}
            </Box>
          </Typography>
          <Typography variant="inherit">
            {company.tel ? (
              <span className={styles.tel}>TEL: {company.tel}</span>
            ) : null}
            {company.fax ? (
              <span className={styles.fax}>FAX: {company.fax}</span>
            ) : null}
            {company.myTel ? (
              <span className={styles.myTel}>担当TEL: {company.myTel}</span>
            ) : null}
          </Typography>
        </Box>
        <Box className={styles.logo}>
          {!props.forAnonymous && (
            <Box className={styles.switch}>
              <Box
                className={`${styles.up} ${
                  Number(props.companyIndex ?? 0) > 0 ? "" : styles.disabled
                }`}
                onClick={onUpClick}
              >
                <KeyboardArrowUpIcon />
              </Box>
              <Box
                className={`${styles.down} ${
                  Number(props.companyIndex ?? 0) < companies.length - 1
                    ? ""
                    : styles.disabled
                }`}
                onClick={onDownClick}
              >
                <KeyboardArrowDownIcon />
              </Box>
            </Box>
          )}
          <img src={logoYoko4c} alt="sumora" />
        </Box>
      </Box>
    );
  }
);
